import { AffiliateCode } from "@decibeltrade/sdk";
import { useTranslations } from "next-intl";
import { css, cx } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { CopyButton } from "@/components/buttons/copy-button";
import { useAnalytics } from "@/contexts/analytics-context";
import type { ReferralLinkCopiedProps } from "@/lib/analytics";

import { SectionCard } from "../../components";
import { referralLink, referralLinkDisplay } from "../../referral-link";

/**
 * Rows shown before the list scrolls instead of stretching the card.
 *
 * An affiliate can be granted any number of codes, and this is a summary card on the
 * overview: past this many rows the list would push the funnel, volume and sign-up
 * sections off the page. Short lists stay uncapped so the common case grows no
 * scroll area.
 */
const MAX_UNCAPPED_ROWS = 5;

export const CodesCard = ({
  codes,
  surface = "affiliate_overview",
}: {
  codes: AffiliateCode[];
  surface?: ReferralLinkCopiedProps["surface"];
}) => {
  const t = useTranslations("affiliates.overview.codesCard");

  return (
    <SectionCard title={t("title")}>
      <div
        className={cx(stack({ gap: "8" }), codes.length > MAX_UNCAPPED_ROWS && scrollAreaStyles)}
      >
        {codes.map((code) => (
          <CodeRow key={code.referral_code} code={code} surface={surface} />
        ))}
      </div>
    </SectionCard>
  );
};

const CodeRow = ({
  code,
  surface,
}: {
  code: AffiliateCode;
  surface: ReferralLinkCopiedProps["surface"];
}) => {
  const t = useTranslations("affiliates.overview.codesCard");
  const analytics = useAnalytics();

  // A reusable code is the one with no cap to state. Any other cap is real, but past the safe
  // integer it has lost precision as a double, so it can only be left out.
  const unlimited = code.source === "reusable";
  const showsCap = !unlimited && Number.isSafeInteger(code.max_usage);
  const count = code.usage_count.toLocaleString("en-US");

  let usage: string;
  if (unlimited) {
    usage = t("usageUncapped", { count });
  } else if (showsCap) {
    usage = t("usageOf", { count, max: code.max_usage.toLocaleString("en-US") });
  } else {
    usage = t("usageCount", { count });
  }

  const pct = code.max_usage > 0 ? Math.min((code.usage_count / code.max_usage) * 100, 100) : 0;

  return (
    <div className={codeRowStyles}>
      <span className={codeNameStyles}>{code.referral_code}</span>
      <div className={flex({ gap: "8", alignItems: "center", flex: "1", minW: "[160px]" })}>
        {/* No track without a cap: a bar that can never fill reads as "barely used". */}
        {showsCap && (
          <div className={usageTrackStyles}>
            <div className={usageFillStyles} style={{ width: `${pct}%` }} />
          </div>
        )}
        <span className={usageCountStyles}>{usage}</span>
      </div>
      <div className={flex({ gap: "8", alignItems: "center", minW: "0" })}>
        {/* Bare host keeps the row narrow; the clipboard gets the full URL so
            pasted links resolve without a scheme. */}
        <span className={codeLinkTextStyles}>{referralLinkDisplay(code.referral_code)}</span>
        <CopyButton
          value={referralLink(code.referral_code)}
          className={linkActionStyles}
          onCopy={() => analytics.affiliates.referralLinkCopied({ surface })}
        />
      </div>
    </div>
  );
};

/**
 * Five 56px rows (32px copy button + 24px padding) and their 8px gaps, plus a 16px
 * sliver of the sixth so the cut reads as "there is more" rather than as the end.
 * A row that wraps on a narrow viewport is taller, so the sliver lands elsewhere —
 * the scrollbar still tells the story.
 */
const scrollAreaStyles = css({
  maxH: "[336px]",
  overflowY: "auto",
});

const codeRowStyles = flex({
  gap: "12",
  alignItems: "center",
  flexWrap: "wrap",
  p: "12",
  bg: "background.secondary",
  rounded: "300",
});

const codeNameStyles = css({
  textStyle: "body.md",
  color: "text.primary",
  minW: "[96px]",
});

const usageTrackStyles = css({
  flex: "1",
  h: "8",
  minW: "[80px]",
  maxW: "[160px]",
  bg: "background.tertiary",
  rounded: "200",
  overflow: "hidden",
});

const usageFillStyles = css({
  h: "full",
  bg: "brandYellow.550",
  rounded: "200",
});

const usageCountStyles = css({
  textStyle: "body.sm",
  color: "text.tertiary",
  whiteSpace: "nowrap",
});

const codeLinkTextStyles = css({
  textStyle: "body.md",
  color: "text.secondary",
  truncate: true,
});

const linkActionStyles = css({
  bg: "background.tertiary",
  p: "8",
  rounded: "secondary",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transitionPreset: "colors",
  _hover: { bg: "background.primary" },
});
